import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";

/**
 * Public landing after a Stripe/OPay checkout. Crucially NOT behind RequireAuth,
 * so a returning customer always sees a confirmation instead of being bounced to
 * a login form (which happened when the redirect crossed origins). When the user
 * is signed in we poll their membership/booking so the webhook-updated state
 * shows without a manual refresh.
 */
export function CheckoutReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { session, authedFetch } = useAuth();

  const type = params.get("type") || (params.get("membership") ? "membership" : params.get("paid") ? "booking" : "membership");
  const cancelled = params.get("status") === "cancel" || params.get("membership") === "cancel" || params.get("paid") === "0";
  const fromApp = params.get("app") === "ios";

  const [tier, setTier] = useState(null);
  const [checking, setChecking] = useState(!cancelled && type === "membership");

  // Native app: bounce straight back so the app closes the in-app browser and syncs
  // membership itself (the app is authenticated; this web context may not be).
  useEffect(() => {
    if (!fromApp) return;
    const t = setTimeout(() => {
      const status = cancelled ? "cancel" : "ok";
      window.location.replace(`ogestudio://checkout-return?type=${type}&status=${status}`);
    }, 600);
    return () => clearTimeout(t);
  }, [fromApp, cancelled, type]);

  // Web: force a reconcile (self-healing, independent of the webhook) then poll so the
  // membership reflects even when the production webhook is delayed/mis-configured.
  useEffect(() => {
    if (fromApp || cancelled || type !== "membership" || !session) { setChecking(false); return; }
    let tries = 0, stop = false;
    (async function poll() {
      try {
        const r = await authedFetch("/api/subscriptions/reconcile", { method: "POST" });
        if (r?.tier && r.tier !== "free") { setTier(r.tier); setChecking(false); return; }
      } catch { /* ignore */ }
      while (!stop && tries < 6) {
        try {
          const out = await authedFetch("/api/subscriptions/me");
          if (out?.tier && out.tier !== "free") { setTier(out.tier); setChecking(false); return; }
        } catch { /* ignore */ }
        tries += 1;
        await new Promise((r) => setTimeout(r, 1800));
      }
      setChecking(false);
    })();
    return () => { stop = true; };
  }, [session, type, cancelled, authedFetch, fromApp]);

  const title = cancelled
    ? "Checkout cancelled"
    : type === "membership"
      ? (tier ? `You're now ${cap(tier)}! 🎉` : "Payment received 🎉")
      : "Payment received 🎉";

  const subtitle = cancelled
    ? "No charge was made. You can try again whenever you're ready."
    : type === "membership"
      ? (checking
          ? "Confirming your membership…"
          : tier
            ? "Your membership is active. Enjoy your perks!"
            : "Your membership is being activated — it'll appear on your profile in a moment.")
      : "Thanks! Your booking is confirmed. You'll find it in your history.";

  const primaryTo = type === "booking" ? "/history" : "/profile";
  const primaryLabel = session
    ? (type === "booking" ? "View my bookings" : "Go to my profile")
    : "Sign in to view it";

  return (
    <div style={{ minHeight: "80vh", display: "grid", placeItems: "center", padding: 24, background: "var(--bg)" }}>
      <div style={{
        maxWidth: 460, width: "100%", textAlign: "center",
        background: "var(--bg-elevated)", border: "1px solid var(--border)",
        borderRadius: 24, padding: "40px 28px", boxShadow: "var(--shadow-lg)",
      }}>
        <div style={{
          width: 84, height: 84, margin: "0 auto 18px", borderRadius: "50%",
          display: "grid", placeItems: "center", fontSize: 38,
          background: cancelled ? "rgba(180,50,50,0.12)" : "linear-gradient(135deg, var(--mauve), var(--plum))",
          color: "#fff",
        }}>
          {cancelled ? "↩︎" : (checking ? "⏳" : "✓")}
        </div>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 26, color: "var(--text)", margin: "0 0 8px" }}>{title}</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6, margin: "0 0 24px" }}>{subtitle}</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          {session ? (
            <button onClick={() => navigate(primaryTo)} style={btnPrimary}>{primaryLabel}</button>
          ) : (
            <Link to={`/login?next=${encodeURIComponent(primaryTo)}`} style={{ ...btnPrimary, textDecoration: "none" }}>{primaryLabel}</Link>
          )}
          <Link to="/" style={btnGhost}>Back to home</Link>
        </div>
      </div>
    </div>
  );
}

function cap(s) { return String(s).charAt(0).toUpperCase() + String(s).slice(1); }
const btnPrimary = {
  display: "inline-block", padding: "12px 22px", borderRadius: 999, border: "none", cursor: "pointer",
  background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))", color: "#fff", fontWeight: 700, fontSize: 15,
};
const btnGhost = {
  display: "inline-block", padding: "12px 22px", borderRadius: 999, textDecoration: "none",
  border: "1px solid var(--border-strong)", color: "var(--text)", fontWeight: 600, fontSize: 15,
};
